import React, { useMemo } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputGroup, InputLabel, Select } from '@components/Common';
import { Terminal } from '@api/queries/terminals';
import { Tank } from '@api/queries/tanks';
import { Customer } from '@api/queries/customers';
import { Product } from '@api/queries/products';
import { ProductMixture } from '@api/queries/productMixtures';
import { EntryType } from '~types/types';
import { EditorMode } from '../hooks/useBookkeepingEntry';
import { BookkeepingFormData } from '../BookkeepingEntryForm';
import { flatMap, includes, uniqBy } from 'lodash';

interface Props {
  mode: EditorMode;
  terminals: Terminal[];
  tanks: Tank[];
  customers: Customer[];
  products: Product[];
  productMixtures: ProductMixture[];
  isLocked: boolean;
  isProductMixture: boolean;
}

const BasicInfoSection: React.FC<Props> = ({
  mode,
  terminals,
  tanks,
  customers,
  products,
  productMixtures,
  isLocked,
  isProductMixture,
}) => {
  const { t } = useTranslation();
  const { control, watch, setValue } = useFormContext<BookkeepingFormData>();

  const terminalId = watch('terminalId');
  const tankId = watch('tankId');
  const customerId = watch('customerId');
  const productId = watch('productId');
  const productAliasId = watch('productAliasId');
  const productMixtureId = watch('productMixtureId');
  const entryType = watch('entryType');

  const isNew = mode === 'new';
  const isEdit = mode === 'edit' || mode === 'approved';
  const isEditingMixture = isProductMixture && isEdit;

  // Terminal options
  const terminalOptions = useMemo(() => {
    let terminalsForSelect = terminals;

    if (isProductMixture) {
      const productmixtureProducts = uniqBy(
        flatMap(productMixtures, (i) =>
          i.mixtures.map((mix) => ({
            terminalId: mix.terminalId,
          }))
        ),
        (i) => i.terminalId
      );
      const mixtureTerminals = productmixtureProducts.map((j) => j.terminalId);
      terminalsForSelect = terminals.filter((i) => includes(mixtureTerminals, i.id));
    }

    return terminalsForSelect.map((t) => ({ value: t.id, label: t.name }));
  }, [terminals, productMixtures, isProductMixture]);

  // Tank options (filtered by terminal)
  const tankOptions = useMemo(() => {
    return tanks
      .filter((t) => t.isEnabled)
      .filter((t) => !terminalId || terminalId === t.terminalId)
      .map((t) => ({ value: t.id, label: t.name }));
  }, [tanks, terminalId]);

  // Customer options (filtered by tank)
  const customerOptions = useMemo(() => {
    const selectedTank = tanks.find((t) => t.id === tankId);

    if (isProductMixture) {
      const mixtureCustomers = productMixtures.map((pm) => pm.customerId);
      return customers
        .filter((c) => includes(mixtureCustomers, c.id))
        .map((c) => ({ value: c.id, label: c.name }));
    }

    return customers
      .filter((c) => c.isActive)
      .filter((c) => selectedTank && selectedTank.customers?.find((tc) => tc.customerId === c.id))
      .map((c) => ({ value: c.id, label: c.name }));
  }, [customers, tanks, tankId, productMixtures, isProductMixture]);

  // Product options (with aliases, filtered by tank)
  const productOptions = useMemo(() => {
    const flattedProducts = products.concat(
      flatMap(products, (p) =>
        p.aliases
          .filter((a) => !a.isDeleted || a.id === productId || a.id === productAliasId)
          .map((a) => ({ ...p, id: a.id, name: a.name, code: a.code }))
      )
    );

    return flattedProducts
      .filter((p) => p.tanks && p.tanks[tankId] !== undefined)
      .map((p) => ({ value: p.id, label: `${p.name} (${p.code})` }));
  }, [products, tankId, productId, productAliasId]);

  // Product mixture options
  const productMixtureOptions = useMemo(() => {
    return productMixtures
      .filter((pm) => pm.isEnabled)
      .filter((pm) => pm.customerId === customerId)
      .map((pm) => ({ value: pm.id, label: pm.name }));
  }, [productMixtures, customerId]);

  // Handle terminal change
  const handleTerminalChange = (newTerminalId: string | null) => {
    setValue('terminalId', newTerminalId);

    // Auto-select tank if only one in terminal
    const tanksInTerminal = tanks.filter((t) => t.terminalId === newTerminalId);
    if (tanksInTerminal.length === 1) {
      handleTankChange(tanksInTerminal[0].id);
    } else {
      setValue('tankId', null);
      setValue('productId', null);
      setValue('customerId', null);
    }
  };

  // Handle tank change
  const handleTankChange = (newTankId: string | null) => {
    setValue('tankId', newTankId);

    if (newTankId) {
      const newTank = tanks.find((t) => t.id === newTankId);
      if (newTank) {
        setValue('terminalId', newTank.terminalId);
        setValue('productId', newTank.productId);
        setValue('customerId', newTank.customers?.[0]?.customerId || null);
      }
    } else {
      setValue('productId', null);
      setValue('customerId', null);
    }
  };

  // Entry type tabs for new entries
  const entryTypeTabs = [
    { value: EntryType.Product, label: t('entry.types.product') },
    { value: EntryType.Mixture, label: t('entry.types.mixture') },
    { value: EntryType.Other, label: t('entry.types.other') },
  ];

  return (
    <>
      {/* Entry Type Tabs (New mode only) */}
      {isNew && (
        <div className="column" style={{ width: '100%', marginBottom: '15px' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            {entryTypeTabs.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => {
                  setValue('entryType', tab.value);
                  setValue('tankId', null);
                  setValue('customerId', null);
                  setValue('productId', null);
                  setValue('productMixtureId', null);
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: entryType === tab.value ? '#007bff' : '#f0f0f0',
                  color: entryType === tab.value ? 'white' : 'black',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Terminal */}
      <div className="column">
        <InputGroup>
          <InputLabel htmlFor="terminalId">{t('entry.terminal')}</InputLabel>
          <Controller
            name="terminalId"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                name="terminalId"
                placeholder={t('select_terminal')}
                value={terminalOptions.find((o) => o.value === field.value) || null}
                onChange={(option) => handleTerminalChange(option?.value || null)}
                isDisabled={isLocked || isEditingMixture}
                options={terminalOptions}
                isClearable
              />
            )}
          />
        </InputGroup>
      </div>

      {/* Tank (not for mixtures) */}
      {!isProductMixture && (
        <div className="column">
          <InputGroup>
            <InputLabel htmlFor="tankId">{t('entry.tank')}</InputLabel>
            <Controller
              name="tankId"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  name="tankId"
                  placeholder={t('select_tank')}
                  value={tankOptions.find((o) => o.value === field.value) || null}
                  onChange={(option) => handleTankChange(option?.value || null)}
                  isDisabled={isLocked}
                  options={tankOptions}
                  isClearable
                  autoFocus={isNew}
                />
              )}
            />
          </InputGroup>
        </div>
      )}

      {/* Customer */}
      <div className="column">
        <InputGroup>
          <InputLabel htmlFor="customerId">{t('entry.customer')}</InputLabel>
          <Controller
            name="customerId"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                name="customerId"
                placeholder={t('select_customer')}
                value={customerOptions.find((o) => o.value === field.value) || null}
                onChange={(option) => {
                  setValue('customerId', option?.value || null);
                  setValue('productMixtureId', null);
                }}
                isDisabled={isLocked || isEditingMixture}
                options={customerOptions}
                isClearable
              />
            )}
          />
        </InputGroup>
      </div>

      {/* Product (not for mixtures) */}
      {!isProductMixture && (
        <div className="column">
          <InputGroup>
            <InputLabel htmlFor="productId">{t('entry.product')}</InputLabel>
            <Controller
              name="productId"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  name="productId"
                  placeholder={t('select_product')}
                  value={productOptions.find((o) => o.value === field.value) || null}
                  onChange={(option) => {
                    setValue('productId', option?.value || null);
                    setValue('productAliasId', option?.value || null);
                  }}
                  isDisabled={isLocked}
                  options={productOptions}
                  isClearable
                />
              )}
            />
          </InputGroup>
        </div>
      )}

      {/* Product Mixture (for mixtures only) */}
      {isProductMixture && (
        <div className="column">
          <InputGroup>
            <InputLabel htmlFor="productMixtureId">{t('entry.product_mixture')}</InputLabel>
            <Controller
              name="productMixtureId"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  name="productMixtureId"
                  placeholder={t('select_product_mixture')}
                  value={productMixtureOptions.find((o) => o.value === field.value) || null}
                  onChange={(option) => setValue('productMixtureId', option?.value || null)}
                  isDisabled={mode !== 'new'}
                  options={productMixtureOptions}
                  isClearable
                />
              )}
            />
          </InputGroup>
        </div>
      )}
    </>
  );
};

export default BasicInfoSection;
